import React from 'react'
import clsx from 'clsx'
import { Loader2 } from 'lucide-react'

type LoaderVariant = 'spinner' | 'dots' | 'pulse' | 'bracket' | 'score' | 'gaming'
type LoaderSize = 'sm' | 'md' | 'lg' | 'xl'

export interface LoaderProps {
  variant?: LoaderVariant
  size?: LoaderSize
  className?: string
  text?: string
  color?: 'default' | 'orange' | 'green' | 'blue' | 'red'
}

const getSizeClasses = (size: LoaderSize) => {
  const sizes = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
    xl: 'h-12 w-12',
  }
  return sizes[size]
}

const getColorClasses = (color: string) => {
  const colors = {
    default: 'text-orange-500',
    orange: 'text-orange-500',
    green: 'text-green-500',
    blue: 'text-blue-500',
    red: 'text-red-500',
  }
  return colors[color as keyof typeof colors] || colors.default
}

const Loader: React.FC<LoaderProps> = ({ 
  variant = 'spinner', 
  size = 'md', 
  className, 
  text,
  color = 'default'
}) => {
  const renderLoader = () => {
    const colorClass = getColorClasses(color)
    
    switch (variant) {
      case 'spinner':
        return (
          <Loader2 
            className={clsx(
              'animate-spin',
              colorClass,
              getSizeClasses(size),
              className
            )}
          />
        )
      
      case 'dots':
        return (
          <div className={clsx('flex space-x-1', className)}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={clsx(
                  'bg-current rounded-full animate-pulse',
                  colorClass,
                  size === 'sm' ? 'h-2 w-2' : 
                  size === 'md' ? 'h-3 w-3' :
                  size === 'lg' ? 'h-4 w-4' : 'h-6 w-6'
                )}
                style={{ animationDelay: `${i * 0.2}s` }}
              />
            ))}
          </div>
        )
      
      case 'pulse':
        return (
          <div 
            className={clsx(
              'bg-current rounded-full animate-pulse',
              colorClass,
              getSizeClasses(size),
              className
            )}
          />
        )
      
      case 'bracket':
        return (
          <div className={clsx('flex items-center space-x-2', className)}>
            <div className={clsx('animate-spin rounded-full border-2 border-t-transparent', colorClass, getSizeClasses(size))} 
                 style={{ borderColor: 'currentColor transparent transparent transparent' }} />
            <div className="flex flex-col space-y-1">
              <div className="h-2 w-8 bg-current rounded animate-pulse" style={{ animationDelay: '0.1s' }} />
              <div className="h-2 w-6 bg-current rounded animate-pulse" style={{ animationDelay: '0.2s' }} />
            </div>
          </div>
        )
      
      case 'score':
        return (
          <div className={clsx('flex space-x-2', className)}>
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={clsx(
                  'bg-current rounded animate-pulse',
                  colorClass,
                  size === 'sm' ? 'h-3 w-1' : 
                  size === 'md' ? 'h-4 w-1' :
                  size === 'lg' ? 'h-6 w-2' : 'h-8 w-2'
                )}
                style={{ 
                  animationDelay: `${i * 0.1}s`,
                  height: `${(i + 1) * (size === 'sm' ? 8 : size === 'md' ? 12 : size === 'lg' ? 16 : 20)}px`
                }}
              />
            ))}
          </div>
        )
      
      case 'gaming':
        return (
          <div className={clsx('relative', getSizeClasses(size), className)}>
            <div className={clsx(
              'absolute inset-0 rounded-full border-2 animate-spin',
              colorClass
            )} style={{ 
              borderColor: 'currentColor transparent currentColor transparent',
              animation: 'spin 1s linear infinite'
            }} />
            <div className={clsx(
              'absolute inset-2 rounded-full border animate-pulse',
              colorClass
            )} style={{ 
              borderColor: 'currentColor',
              opacity: 0.5,
              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
            }} />
          </div>
        )
      
      default:
        return null
    }
  }

  if (text) {
    return (
      <div className="flex items-center space-x-3">
        {renderLoader()}
        <span className="text-text-secondary text-sm">{text}</span>
      </div>
    )
  }

  return renderLoader()
}

// Skeleton Component
export interface SkeletonProps {
  className?: string
  width?: string | number
  height?: string | number
  circle?: boolean
  lines?: number
  spacing?: string
}

const Skeleton: React.FC<SkeletonProps> = ({
  className,
  width,
  height,
  circle = false,
  lines = 1,
  spacing = '0.5rem',
}) => {
  const baseClasses = 'animate-pulse bg-dark-500 rounded'
  
  if (lines > 1) {
    return (
      <div className={clsx('space-y-2', className)} style={{ gap: spacing }}>
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            className={baseClasses}
            style={{
              width: i === lines - 1 ? '75%' : width || '100%',
              height: height || '1rem',
            }}
          />
        ))}
      </div>
    )
  }

  return (
    <div
      className={clsx(
        baseClasses,
        circle && 'rounded-full',
        className
      )}
      style={{
        width: width || (circle ? '3rem' : '100%'),
        height: height || (circle ? '3rem' : '1rem'),
      }}
    />
  )
}

// Page Loader Component
export interface PageLoaderProps {
  text?: string
  className?: string
}

const PageLoader: React.FC<PageLoaderProps> = ({ 
  text = 'Loading...', 
  className 
}) => {
  return (
    <div className={clsx(
      'flex items-center justify-center min-h-[400px]',
      className
    )}>
      <div className="text-center">
        <Loader variant="spinner" size="lg" />
        <p className="mt-4 text-text-secondary text-sm">{text}</p>
      </div>
    </div>
  )
}

// Inline Loader Component
export interface InlineLoaderProps {
  text?: string
  className?: string
}

const InlineLoader: React.FC<InlineLoaderProps> = ({ 
  text = 'Loading...', 
  className 
}) => {
  return (
    <div className={clsx('flex items-center space-x-2', className)}>
      <Loader variant="spinner" size="sm" />
      <span className="text-text-secondary text-sm">{text}</span>
    </div>
  )
}

export { Loader, Skeleton, PageLoader, InlineLoader }